import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseCustomFieldViewComponent } from '../../../../../base/form';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import {
  arr_first,
  arr_map,
  arr_reduce,
  blank,
  data_get,
  data_nullify,
  fallback,
  observe,
  OptionalValue,
  spf
} from '../../../../../shared/utils/common';
import { ApiService } from '../../../../../services/api.service';
import { AnswerableChecklistPrompt, Checklist, ChecklistPrompt, ChecklistPromptGroup, translate_period } from '../../../../checklists';
import { ViewService } from '../../../../../services/view.service';
import { tap } from 'rxjs/operators';
import { Field, ModuleMetadata } from '../../../../../services/form.service';
import { TranslateService } from '@ngx-translate/core';
import { memoize } from 'lodash-es';
import { AssetTypeFault, AssetTypeFaultSeverity, AssetTypeVariant } from '../../types';
import { RelateOption } from '../../../../../shared/components/forms/input/relate/relate-input.component';
import { FieldmagicDropdownOption } from '../../../../../shared/components/forms/input/dropdown/dropdown.component';

@Component({
  selector: 'asset-types-view-inspection-checklist',
  template: `
    <div
      *ngIf="fetching$ | async"
      fieldmagicGridRow
    >
      <div fieldmagicGridColumn="12">
        <div
          class="d-flex justify-content-center w-100"
        >
          <fieldmagic-icon [spin]="true"></fieldmagic-icon>
        </div>
      </div>
    </div>

    <ng-container>
      <div fieldmagicGridRow>
        <div fieldmagicGridColumn="12">
          <div class="d-flex d-flex-gap flex-column flex-column">
            <div class="d-flex justify-content-between align-items-center w-100">
              <fieldmagic-text
                purpose="title"
                content="Prompts"
                class="w-50"
              >
              </fieldmagic-text>

              <div class="d-flex d-flex-gap justify-content-end w-50">
                <fieldmagic-relate-input
                  class="w-100"
                  [(ngModel)]="variant"
                  [standalone]="true"
                  placeholder="Select a variant"
                  moduleName="asset_type_variants"
                  [filter]="{
                    asset_type_id: id$ | async
                  }"
                  (ngModelChange)="onVariantChange()"
                >
                </fieldmagic-relate-input>

                <fieldmagic-dropdown-input
                  class="w-100"
                  [(ngModel)]="checklist"
                  [options]="checklists$ | async"
                  [standalone]="true"
                  (ngModelChange)="onChecklistChanged()"
                  placeholder="Select a checklist.."
                  [clearable]="false"
                >
                </fieldmagic-dropdown-input>
              </div>
            </div>

            <table class="table table-bordered">
              <thead>
                <tr>
                  <td width="20%">
                    <fieldmagic-text
                      purpose="table-header"
                      content="Prompt"
                    >
                    </fieldmagic-text>
                  </td>

                  <td width="10%">
                    <fieldmagic-text
                      purpose="table-header"
                      content="Type"
                    >
                    </fieldmagic-text>
                  </td>

                  <td width="20%">
                    <fieldmagic-text
                      purpose="table-header"
                      content="Value"
                    >
                    </fieldmagic-text>
                  </td>

                  <td width="5%">
                    <fieldmagic-icon
                      position="center"
                      icon="asterisk"
                      class="text-danger"
                      tooltip="{{ 'tooltip_required' | translate }}"
                    >
                    </fieldmagic-icon>
                  </td>

                  <td width="5%">
                    <fieldmagic-icon
                      position="center"
                      icon="desktop"
                      tooltip="{{ 'tooltip_report_visibility' | translate }}"
                    ></fieldmagic-icon>
                  </td>

                  <td width="5%">
                    <fieldmagic-icon
                      position="center"
                      icon="file-image"
                      tooltip="{{ 'add_image_to_prompt' | translate }}"
                    ></fieldmagic-icon>
                  </td>

                  <td width="5%">
                    <fieldmagic-icon
                      position="center"
                      icon="file-image"
                      tooltip="Require atleast 1 image to the prompt"
                      [withRequiredMarker]="true"
                    ></fieldmagic-icon>
                  </td>
                </tr>
              </thead>

              <tbody>
                <ng-container *ngIf="prompts$ | async as prompts">
                  <tr
                    *ngIf="prompts | blank"
                    class="text-center"
                  >
                    <td colspan="7">{{ error$ | async }}</td>
                  </tr>

                  <ng-container *ngFor="let prompt of prompts">
                    <ng-container *ngIf="prompt.type != 'group'">
                      <ng-container
                        [ngTemplateOutlet]="promptTmp"
                        [ngTemplateOutletContext]="{
                          prompt: prompt
                        }"
                      ></ng-container>
                    </ng-container>

                    <ng-container *ngIf="prompt.type == 'group'">
                      <tr>
                        <td colspan="7">{{ computeGroupName(prompt) }}</td>
                      </tr>
                      <ng-container  *ngFor="let subprompt of prompt.prompts">
                        <ng-container
                          *ngIf="subprompt.type != 'group'"
                          [ngTemplateOutlet]="promptTmp"
                          [ngTemplateOutletContext]="{
                            prompt: subprompt,
                            as_sub: true
                          }"
                        ></ng-container>
                      </ng-container>
                    </ng-container>
                  </ng-container>

                  <ng-template
                    #promptTmp
                    let-prompt="prompt"
                    let-as_sub="as_sub"
                  >
                    <tr
                      [class.inspection-group-subprompt-row]="as_sub"
                    >
                      <td>
                        <span class="inspection-prompt-name">{{ prompt.prompt }}</span>
                      </td>
                      <td>
                        <span class="inspection-prompt-type">
                          {{ prompt.type | translate }}
                        </span>
                      </td>
                      <td>
                        <span *ngIf="prompt.type != 'instructions'" class="inspection-prompt-value">
                          {{ computePromptValue(prompt) | placeholdWithString }}
                        </span>

                        <div *ngIf="prompt.type == 'instructions'">
                          <fieldmagic-textarea-input
                            [ngModel]="prompt | data_get: 'value.instructions_text'"
                            [wyswyg]="true"
                            [disabled]="true"
                          >
                          </fieldmagic-textarea-input>
                        </div>
                      </td>
                      <td class="text-center">
                        <span class="inspection-prompt-is-required">{{ (prompt.is_required ? 'Yes' : 'No') | translate }}</span>
                      </td>
                      <td class="text-center">
                        <span class="inspection-prompt-is-report-visible">{{ (prompt.is_visible_in_reports ? 'Yes' : 'No') | translate }}</span>
                      </td>
                      <td class="text-center">
                        <span class="inspection-prompt-with-image">{{ (prompt.is_image_enabled ? 'Yes' : 'No') | translate }}</span>
                      </td>
                      <td class="text-center">
                        <span class="inspection-prompt-image-required">{{ (prompt.is_image_required ? 'Yes' : 'No') | translate }}</span>
                      </td>
                    </tr>

                    <ng-container *ngIf="computeFaults(prompt) as faults">
                      <tr *ngIf="(faults | filled)">
                        <td colspan="7">
                          <fieldmagic-indent>
                            <span matTooltip="{{ 'Defects' | translate }}">{{ faults }}</span>
                          </fieldmagic-indent>
                        </td>
                      </tr>
                    </ng-container>
                  </ng-template>
                </ng-container>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ng-container>
  `,
  styles: [
    `.inspection-group-subprompt-row td:first-child span::before {
      content: url('/../../../../../assets/img/indent.png');
      width: 13px;
      height: 13px;
      margin-top: 3px;
      margin-right: 6px;
    }`
  ]
})
export class AssetViewTypeInspectionChecklistComponent extends BaseCustomFieldViewComponent implements OnInit, OnDestroy {
  variant?: OptionalValue<AssetTypeVariantSelectedOption>;
  checklist?: OptionalValue<AssetTypeInspectionChecklistOption>;

  readonly fetching$ = new BehaviorSubject<boolean>(true);

  readonly checklist$ = new BehaviorSubject<OptionalValue<AssetTypeViewedInspectionChecklist>>(null);
  readonly error$ = new BehaviorSubject<OptionalValue<string>>(null);
  readonly id$ = new BehaviorSubject<OptionalValue<string>>(null);
  readonly checklists$ = new BehaviorSubject<AssetTypeInspectionChecklistOption[]>([]);

  readonly prompts$ = new BehaviorSubject<ChecklistPrompt[]>([]);

  readonly _variant$ = new Subject<OptionalValue<AssetTypeVariantSelectedOption>>();
  readonly _faults$ = new BehaviorSubject<Record<string, IndexedAssetTypeFault>>({});

  private _onChecklistFetchingSubscription?: Subscription;
  private _onVariantChangeSubscription?: Subscription;

  constructor(
    private readonly _api: ApiService,
    private readonly _view: ViewService,
    private readonly _translator: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    const id = data_get(this._view.getViewRecord(), 'id');

    if (blank(id)) {
      this.fetching$.next(false);
      this.error$.next('Unable to view checklist due to missing record id.');
      return;
    }

    this.id$.next(id);
    this._initChecklist();

    this._onVariantChangeSubscription = this._variant$.subscribe(() => this._initChecklist());
  }

  ngOnDestroy(): void {
    if (this._onChecklistFetchingSubscription) {
      this._onChecklistFetchingSubscription.unsubscribe();
    }

    if (this._onVariantChangeSubscription) {
      this._onVariantChangeSubscription.unsubscribe();
    }
  }

  computePromptValue(prompt: ChecklistPrompt<AnswerableChecklistPrompt>): OptionalValue<string> {
    const special = [
      'pass/fail',
      'dropdown',
      'field_entry',
    ];

    if (!special.includes(prompt.type)) {
      return null;
    }

    if (prompt.type == 'pass/fail') {
      const options = [
        ...fallback(data_get(prompt, 'value.pass'), {
          fallback: () => [],
        }),
        ...fallback(data_get(prompt, 'value.fail'), {
          fallback: () => [],
        }),
        ...fallback(data_get(prompt, 'value.pending'), {
          fallback: () => [],
        }),
      ];

      return options.join(', ');
    }

    if (prompt.type == 'dropdown') {
      const options = fallback(data_get(prompt, 'value.dropdown'), {
        fallback: () => [],
      });

      return options.join(', ');
    }

    if (prompt.type == 'field_entry') {
      const target: OptionalValue<string> = data_get(prompt, 'value.field_entry');

      if (blank(target)) {
        return null;
      }

      const [moduleName, attributeName] = target.split(/\-(.*)/, 2);
      const field: OptionalValue<Field> = data_get(this.checklist$.value, ['field_entries', moduleName, attributeName]);

      if (blank(field)) {
        return null;
      }

      return spf('%s: %s', {
        args: [
          moduleName == 'assets' ? this._translator.instant('Asset') : this._translator.instant('Attribute'),
          this._translator.instant(field.label),
        ],
      });
    }

    return null;
  }

  computeGroupName = memoize((prompt: ChecklistPromptGroup) => {
    if (prompt.available_period == 'always') {
      return prompt.group_name;
    }

    return spf('%s (%s)', {
      args: [prompt.group_name, translate_period(prompt.available_period, {
        translator: this._translator,
      })],
    });
  });

  onVariantChange() {
    this.checklist = null;
    this.checklists$.next([]);

    this._variant$.next(this.variant);
  }

  onChecklistChanged = () => this._initChecklist();

  computeFaults(prompt: ChecklistPrompt<AnswerableChecklistPrompt>): OptionalValue<string> {
    const ids = prompt.faults;

    if (blank(ids)) {
      return null;
    }

    const parts = [];
    const faults = this._faults$.value;

    if (blank(faults)) {
      return null;
    }

    for (const id of ids) {
      const fault = faults[id];

      if (blank(fault)) {
        continue;
      }

      parts.push(spf('(%s) %s', {
        args: [fault.severity, fault.fault],
      }));
    }

    if (blank(parts)) {
      return null;
    }

    return parts.join(', ');
  }

  private _initChecklist(): void {
    this._onChecklistFetchingSubscription = observe({
      after: () => this.fetching$.next(false),
      observable: () => this._api.callPrivate$({
        action: 'post',
        path: '/asset_types/inspection_checklists',
        expectsNewFormat: true,
        body: {
          'checklist_id': data_nullify(data_get(this.checklist, 'id')),
          'asset_type_id': this.id$.value,
          'is_view': true,
          'asset_type_variant_id': data_get(this.variant, 'id'),
        },
        onSuccessResponse: (response) => response.get('items'),
      }),
    })
      .pipe(
        tap(() => this.error$.next(null)),
      )
      .subscribe((checklists) => {
        if (blank(checklists)) {
          this.error$.next('No inspection checklist available');
          this.prompts$.next([]);
          this.checklist = null;
          this.checklists$.next([]);

          return;
        }

        if (blank(this.checklist)) {
          this.checklists$.next([]);
        }

        const checklist = arr_first(checklists);

        this.checklist$.next(checklist);

        for (const question of checklist.questions) {
          if (question.name == 'per_asset_prompts') {
            this.prompts$.next(question.prompts);
          }
        }

        if (blank(this.prompts$.value)) {
          this.error$.next('No available prompts');
        }

        const severities = fallback(data_get(checklist, 'asset_type_severities'), {
          to: () => [],
        });

        const faults = arr_reduce<IndexedAssetTypeFault[], AssetTypeFaultSeverity>(severities, {
          initial_value: [],
          reducer: (initial, severity) => {
            initial.push(...arr_map<IndexedAssetTypeFault, AssetTypeFault>(fallback<AssetTypeFault[]>(severity.faults, {
              to: () => [],
            }), {
              transformer: (fault) => ({
                ...fault,
                severity: severity.severity,
              })
            }));

            return initial;
          },
        });

        const indexed = {};

        for (const fault of faults) {
          indexed[fault.id] = fault;
        }

        this._faults$.next(indexed);

        if (blank(this.checklist)) {
          this.checklists$.next(arr_map(checklists, {
            transformer: (checklist) => ({
              id: data_get(checklist, 'id'),
              text: data_get(checklist, 'name'),
            }),
          }));
        }

        this.checklist = {
          id: data_get(checklist, 'id'),
          text: data_get(checklist, 'text'),
        };
      });
  }
}

type AssetTypeViewedInspectionChecklist = Checklist & {
  field_entries: {
    assets: OptionalValue<ModuleMetadata>,
    custom_asset_types: OptionalValue<ModuleMetadata>,
  };
}

type AssetTypeVariantSelectedOption = RelateOption<AssetTypeVariant>;
type IndexedAssetTypeFault = AssetTypeFault & {
  severity: string;
};

type AssetTypeInspectionChecklistOption = FieldmagicDropdownOption<{
  id: string;
}>;